import { FC, PropsWithChildren } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { brandColors } from '@/app/constants';

// Helper function to get color based on value using Tailwind RGB values
const getValueColor = (value: number, thresholds: { good: number; medium: number }) => {
  if (value >= thresholds.good) return brandColors.green;
  if (value >= thresholds.medium) return brandColors.yellow;
  return brandColors.red;
};

// Custom Tooltip Component
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    color: string;
    name: string;
    value: number;
  }>;
  label?: string;
}

export const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="px-3 py-2 border border-custom-mediumSilver rounded-lg bg-white shadow-[inset_0_1px_0_#ffffff,inset_1px_0_0_#ffffff,2px_2px_4px_rgba(0,0,0,0.05)]">
        <p className="text-custom-black text-xs">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="text-sm font-medium">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

type CustomCardProps = FC<{ title?: string; type?: 'light' | 'blue'; className?: string } & PropsWithChildren>;

export const CustomCard: CustomCardProps = ({ title, type = 'light', className, children }) => {
  return (
    <div
      className={`bg-white rounded-lg border p-4 ${type === 'light' && 'border-custom-lightSilver'} ${
        type === 'blue' && 'border-custom-blue/30'
      } ${className}`}
    >
      {title ? (
        <h3
          className={`text-md font-semibold mb-1 ${type === 'light' && 'text-custom-black'} ${
            type === 'blue' && 'text-custom-blue'
          }`}
        >
          {title}
        </h3>
      ) : null}
      {children}
    </div>
  );
};

// Customer Health Score Component
interface CustomerHealthScoreProps {
  healthScore: {
    averageScore: number;
    scoreChange: {
      value: number;
      percentageChange: number;
      isPositive: boolean;
      comparisonPeriod: string;
    };
    distribution: {
      excellent: number;
      good: number;
      fair: number;
      poor: number;
      critical: number;
    };
  };
}

export const CustomerHealthScore = ({ healthScore }: CustomerHealthScoreProps) => {
  const data = [
    { name: 'filled', value: healthScore.averageScore },
    { name: 'empty', value: 100 - healthScore.averageScore },
  ];

  // Check if all distribution values are zero
  const hasNoData =
    Object.values(healthScore.distribution).every((value) => value === 0) && healthScore.averageScore === 0;

  return (
    <CustomCard title="Customer Health Score">
      {hasNoData ? (
        <div className="text-center text-custom-darkSilver py-8">No health score data available</div>
      ) : (
        <>
          <div className="flex items-center justify-center">
            <div className="relative w-40 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius="75%"
                    outerRadius="100%"
                    paddingAngle={0}
                    startAngle={90}
                    endAngle={-270}
                    dataKey="value"
                  >
                    {data.map((entry) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={
                          entry.name === 'empty'
                            ? '#f0f0f2'
                            : getValueColor(healthScore.averageScore, { good: 80, medium: 60 })
                        }
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold text-custom-black">{healthScore.averageScore}%</span>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-custom-darkSilver">Excellent</span>
              <span className="font-semibold text-custom-green">{healthScore.distribution.excellent}%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-medium text-custom-darkSilver">Good</span>
              <span className="font-semibold text-custom-green">{healthScore.distribution.good}%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-medium text-custom-darkSilver">Fair</span>
              <span className="font-semibold text-custom-yellow">{healthScore.distribution.fair}%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-medium text-custom-darkSilver">Poor</span>
              <span className="font-semibold text-custom-yellow">{healthScore.distribution.poor}%</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="font-medium text-custom-darkSilver">Critical</span>
              <span className="font-semibold text-custom-red">{healthScore.distribution.critical}%</span>
            </div>
          </div>
          <div className="mt-3 text-center">
            <div
              className={`text-sm font-medium ${
                healthScore.scoreChange.isPositive ? 'text-custom-green' : 'text-custom-red'
              }`}
            >
              {healthScore.scoreChange.isPositive ? '↑' : '↓'}{' '}
              {Math.abs(healthScore.scoreChange.percentageChange).toFixed(1)}%{healthScore.scoreChange.comparisonPeriod}
            </div>
          </div>
        </>
      )}
    </CustomCard>
  );
};

// Monthly Churn Rate Component
interface MonthlyChurnRateProps {
  churnRate: {
    current?: number;
    value?: number;
    change: {
      value: number;
      percentageChange: number | null;
      percentageChangeMeaningful?: boolean;
      isPositive: boolean;
      comparisonPeriod: string;
    };
  };
}

export const MonthlyChurnRate = ({ churnRate }: MonthlyChurnRateProps) => {
  const value = (churnRate as any).value ?? (churnRate as any).current;
  // When meaningful: show relative % change (e.g. "↑ 20% vs last month" = churn rate increased by 20% relative to last month)
  // When previous month was 0%: show absolute change in percentage points (e.g. "↑ 16.7% pts vs last month")
  const showRelativePercent =
    churnRate.change.percentageChange != null &&
    (churnRate.change.percentageChangeMeaningful === undefined || churnRate.change.percentageChangeMeaningful);

  return (
    <CustomCard title="Monthly Churn Rate">
      <div className="text-4xl font-bold text-custom-black mb-1">{value}%</div>
      <div className="text-sm">
        <span className={`font-bold ${churnRate.change.isPositive ? 'text-custom-red' : 'text-custom-green'}`}>
          {churnRate.change.isPositive ? '↓' : '↑'}{' '}
          {showRelativePercent ? (
            <>{Math.abs(churnRate.change.percentageChange ?? 0).toFixed(1)}%&nbsp;</>
          ) : (
            <>{Math.abs(churnRate.change.value).toFixed(1)}% pts&nbsp;</>
          )}
        </span>
        <span className="font-normal text-custom-darkSilver">{churnRate.change.comparisonPeriod}</span>
      </div>
    </CustomCard>
  );
};

// Monthly Recurring Revenue Component
interface MonthlyRecurringRevenueProps {
  revenue: {
    current?: number;
    value?: number;
    change: {
      value: number;
      percentageChange: number | null;
      percentageChangeMeaningful?: boolean;
      isPositive: boolean;
      comparisonPeriod: string;
    };
  };
}

export const MonthlyRecurringRevenue = ({ revenue }: MonthlyRecurringRevenueProps) => {
  const showPercentage =
    revenue.change.percentageChange != null &&
    (revenue.change.percentageChangeMeaningful === undefined || revenue.change.percentageChangeMeaningful);

  return (
    <CustomCard title="Monthly Recurring Revenue">
      <div className="text-4xl font-bold text-custom-black mb-1">
        ${((revenue as any).value ?? (revenue as any).current).toLocaleString()}
      </div>
      <div className="text-sm">
        <span className={`font-bold ${revenue.change.isPositive ? 'text-custom-green' : 'text-custom-red'}`}>
          {showPercentage ? (
            <>
              {revenue.change.isPositive ? '↑' : '↓'} {Math.abs(revenue.change.percentageChange ?? 0).toFixed(1)}%&nbsp;
            </>
          ) : (
            <>
              {revenue.change.isPositive ? '↑' : '↓'} {revenue.change.value >= 0 ? '+' : ''}$
              {Math.abs(revenue.change.value).toLocaleString()}&nbsp;
            </>
          )}
        </span>
        <span className="font-normal text-custom-darkSilver">{revenue.change.comparisonPeriod}</span>
      </div>
    </CustomCard>
  );
};

// Churn Risk Segments Component
interface ChurnRiskSegment {
  segment: string; // "High Risk" | "Medium Risk" | "Low Risk"
  count: number;
  percentage: number;
  avgDaysInactive: number;
}

interface ChurnRiskSegmentsProps {
  riskSegments?: ChurnRiskSegment[];
  // Fallback props for backward compatibility
  customersAtRisk?: {
    total: number;
    high: number;
    moderate: number;
    change: {
      value: number;
      percentageChange: number;
      isPositive: boolean;
      comparisonPeriod: string;
    };
  };
  totalCustomers?: number;
}

export const ChurnRiskSegments = ({ riskSegments, customersAtRisk, totalCustomers }: ChurnRiskSegmentsProps) => {
  // Use riskSegments from churn data if available, otherwise fall back to calculating from customersAtRisk
  let segments: Array<{ segment: string; count: number; percentage: number; color: string; textColor: string }> = [];

  if (riskSegments && riskSegments.length > 0) {
    // Use data from churn.riskSegments
    segments = riskSegments.map((seg) => {
      let color = brandColors.darkSilver;
      let textColor = 'text-gray-500';

      if (seg.segment === 'High Risk') {
        color = brandColors.red;
        textColor = 'text-red-500';
      } else if (seg.segment === 'Medium Risk') {
        color = brandColors.yellow;
        textColor = 'text-yellow-500';
      } else if (seg.segment === 'Low Risk') {
        color = brandColors.green;
        textColor = 'text-green-500';
      }

      return {
        segment: seg.segment,
        count: seg.count,
        percentage: seg.percentage,
        color,
        textColor,
      };
    });
  } else if (customersAtRisk && totalCustomers) {
    // Fallback to old calculation method
    const { total, high, moderate } = customersAtRisk;
    const low = totalCustomers - total;

    segments = [
      {
        segment: 'High Risk',
        count: high,
        percentage: totalCustomers > 0 ? Math.round((high / totalCustomers) * 100) : 0,
        color: brandColors.red,
        textColor: 'text-red-500',
      },
      {
        segment: 'Moderate Risk',
        count: moderate,
        percentage: totalCustomers > 0 ? Math.round((moderate / totalCustomers) * 100) : 0,
        color: brandColors.yellow,
        textColor: 'text-yellow-500',
      },
      {
        segment: 'Low Risk',
        count: low,
        percentage: totalCustomers > 0 ? Math.round((low / totalCustomers) * 100) : 0,
        color: brandColors.green,
        textColor: 'text-green-500',
      },
    ].filter((segment) => segment.count > 0);
  }

  if (segments.length === 0) {
    return (
      <CustomCard title="Churn Risk Segments">
        <div className="text-center text-custom-darkSilver py-8">No risk segment data available</div>
      </CustomCard>
    );
  }

  return (
    <CustomCard title="Churn Risk Segments">
      <div className="flex items-center justify-center">
        <div className="relative w-40 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={segments} dataKey="count" nameKey="segment" cx="50%" cy="50%" outerRadius={70}>
                {segments.map((entry: any, index: number) => {
                  return <Cell key={`cell-${index}`} fill={entry.color} />;
                })}
              </Pie>

              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-4 space-y-1">
        {segments.map((segment, index) => {
          return (
            <div key={index} className="flex justify-between items-center text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: segment.color }} />
                <span className="font-medium text-custom-darkSilver">{segment.segment}</span>
              </div>
              <div className="text-right">
                <span className="font-semibold" style={{ color: segment.color }}>
                  {segment.count} ({segment.percentage}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </CustomCard>
  );
};

// Subscription Movement Component
interface SubscriptionMovementProps {
  subscriptionMovements: Array<{
    movement: string;
    count: number;
    mrrImpact: number;
    percentageOfTotal: number;
  }>;
}

export const SubscriptionMovement = ({ subscriptionMovements }: SubscriptionMovementProps) => {
  if (subscriptionMovements.length === 0) {
    return (
      <CustomCard title="Subscription Movement">
        <div className="text-center text-custom-darkSilver py-8">No subscription movement data available</div>
      </CustomCard>
    );
  }

  const getMovementIcon = (movement: string) => {
    switch (movement) {
      case 'upgrade':
        return '↑';
      case 'downgrade':
        return '↓';
      case 'churn':
        return '✕';
      case 'new':
        return '+';
      case 'reactivation':
        return '↻';
      default:
        return '•';
    }
  };

  const getMovementColor = (movement: string) => {
    switch (movement) {
      case 'upgrade':
        return 'text-custom-green';
      case 'downgrade':
        return 'text-custom-yellow';
      case 'churn':
        return 'text-custom-red';
      case 'new':
        return 'text-custom-blue';
      case 'reactivation':
        return 'text-custom-green';
      default:
        return 'text-custom-darkSilver';
    }
  };

  return (
    <CustomCard title="Subscription Movement">
      <div className="space-y-4">
        {subscriptionMovements.map((movement, index) => (
          <div key={index} className="flex justify-between items-center">
            <div>
              <div className="text-custom-darkSilver capitalize">{movement.movement}s</div>
              <div className={`font-bold text-xl mt-1 ${getMovementColor(movement.movement)}`}>
                {movement.count}
                <span className="text-sm ml-2 font-normal text-custom-darkSilver">({movement.percentageOfTotal}%)</span>
              </div>
            </div>
            <div className="text-right">
              <div className={`text-2xl ${getMovementColor(movement.movement)}`}>
                {getMovementIcon(movement.movement)}
              </div>
              <div className="text-xs text-custom-darkSilver">${movement.mrrImpact.toLocaleString()} MRR</div>
            </div>
          </div>
        ))}
      </div>
    </CustomCard>
  );
};

// Key Revenue Metrics Component
interface KeyRevenueMetricsProps {
  revenue: {
    arpu: number;
    ltv: number;
  };
  engagement: {
    dailyActiveUsers: number;
    weeklyActiveUsers: number;
    monthlyActiveUsers: number;
  };
}

export const KeyRevenueMetrics = ({ revenue, engagement }: KeyRevenueMetricsProps) => {
  return (
    <CustomCard title="Key Revenue Metrics">
      <div className="grid grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <div className="text-gray-400">ARPU</div>
            <div className="text-2xl font-bold text-custom-textBlack" style={{ fontSize: '1.5rem' }}>
              ${revenue.arpu.toFixed(2)}
            </div>
          </div>
          <div>
            <div className="text-gray-400">LTV</div>
            <div className="text-2xl font-bold text-custom-textBlack" style={{ fontSize: '1.5rem' }}>
              ${revenue.ltv.toLocaleString()}
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <div>
            <div className="text-gray-400">Daily Active</div>
            <div className="text-2xl font-bold text-custom-textBlack" style={{ fontSize: '1.5rem' }}>
              {engagement.dailyActiveUsers}
            </div>
            <div className="text-sm text-gray-400">
              {((engagement.dailyActiveUsers / engagement.monthlyActiveUsers) * 100).toFixed(1)}% of MAU
            </div>
          </div>
          <div>
            <div className="text-gray-400">Weekly Active</div>
            <div className="text-2xl font-bold text-custom-textBlack" style={{ fontSize: '1.5rem' }}>
              {engagement.weeklyActiveUsers}
            </div>
            <div className="text-sm text-gray-400">
              {((engagement.weeklyActiveUsers / engagement.monthlyActiveUsers) * 100).toFixed(1)}% of MAU
            </div>
          </div>
        </div>
      </div>
    </CustomCard>
  );
};

// Revenue by Plan Component
interface RevenueByPlanProps {
  revenue: {
    byPlan: Array<{
      plan: string;
      revenue: number;
      customerCount: number;
      percentageOfTotal: number;
      revenueChange: {
        value: number;
        percentageChange: number;
        isPositive: boolean;
        comparisonPeriod: string;
      };
    }>;
  };
}

export const RevenueByPlan = ({ revenue }: RevenueByPlanProps) => {
  // Check if revenue has byPlan property, otherwise return empty state
  if (!revenue || !revenue.byPlan || !Array.isArray(revenue.byPlan)) {
    return (
      <CustomCard title="Revenue by Plan">
        <div className="text-center text-gray-500 py-8">No plan data available</div>
      </CustomCard>
    );
  }

  // Consolidate duplicate plans by summing their revenue and customer counts
  const consolidatedPlans = revenue.byPlan.reduce((acc: any[], plan: any) => {
    const existing = acc.find((p: any) => p.plan === plan.plan);
    if (existing) {
      existing.revenue += plan.revenue;
      existing.customerCount += plan.customerCount;
      existing.percentageOfTotal += plan.percentageOfTotal;
    } else {
      acc.push({ ...plan });
    }
    return acc;
  }, [] as typeof revenue.byPlan);

  if (consolidatedPlans.length === 0) {
    return (
      <CustomCard title="Revenue by Plan">
        <div className="text-center text-custom-darkSilver py-8">No revenue data available</div>
      </CustomCard>
    );
  }

  return (
    <CustomCard title="Revenue by Plan">
      <ResponsiveContainer width="100%" height={240}>
        <PieChart>
          <Pie
            data={consolidatedPlans}
            dataKey="revenue"
            nameKey="plan"
            cx="50%"
            cy="50%"
            outerRadius={80}
            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
          >
            {consolidatedPlans.map((entry: any, index: number) => {
              const colors = ['rgb(239 68 68)', 'rgb(255 197 49)', 'rgb(46 216 168)', 'rgb(99 102 241)']; // red-500, orange-400, green-500, indigo-500

              return <Cell key={`cell-${index}`} fill={colors[index] || 'rgb(var(--color-purple-400))'} />;
            })}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>

      <div className="mt-4 space-y-2">
        {consolidatedPlans.map((plan: any, index: number) => (
          <div key={index} className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-2">
              <div
                className={`w-3 h-3 rounded-full`}
                style={{
                  backgroundColor: ['rgb(239 68 68)', 'rgb(255 197 49)', 'rgb(46 216 168)', 'rgb(99 102 241)'][index],
                }}
              />
              <span className="font-medium text-custom-darkSilver capitalize">{plan.plan}</span>
            </div>
            <div className="text-right">
              <span className="font-semibold text-custom-black">${plan.revenue.toLocaleString()}</span>
              <span className="text-xs text-custom-darkSilver ml-2">({plan.customerCount} customers)</span>
            </div>
          </div>
        ))}
      </div>
    </CustomCard>
  );
};

// Net Subscription Change Component
interface NetSubscriptionChangeProps {
  subscriptionChanges: {
    new: number;
    canceled: number;
    net: number;
    change: {
      value: number;
      percentageChange: number | null;
      percentageChangeMeaningful?: boolean;
      isPositive: boolean;
      comparisonPeriod: string;
    };
  };
}

export const NetSubscriptionChange = ({ subscriptionChanges }: NetSubscriptionChangeProps) => {
  // Check if subscriptionChanges data exists
  if (!subscriptionChanges) {
    return (
      <CustomCard title="Net Subscription Change">
        <div className="text-center text-gray-500 py-8">No subscription data available</div>
      </CustomCard>
    );
  }

  const { new: newSubscriptions, canceled, net } = subscriptionChanges;
  const { change } = subscriptionChanges;
  const showPercentage =
    change.percentageChange != null &&
    (change.percentageChangeMeaningful === undefined || change.percentageChangeMeaningful);

  return (
    <CustomCard title="Net Subscription Change">
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div
            className={`text-6xl font-bold mb-4 ${
              net > 0 ? 'text-custom-green' : net < 0 ? 'text-custom-red' : 'text-custom-darkSilver'
            }`}
          >
            {net > 0 ? '+' : ''}
            {net}
          </div>
          <div className="flex items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-custom-green">+</span>
              <span className="text-custom-darkSilver">{newSubscriptions}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-custom-red">✕</span>
              <span className="text-custom-darkSilver">{canceled}</span>
            </div>
          </div>
          <div className="mt-4 text-sm text-custom-darkSilver">
            {showPercentage ? (
              <>
                {change.isPositive ? '↑' : '↓'} {Math.abs(change.percentageChange ?? 0).toFixed(1)}%{' '}
                {change.comparisonPeriod}
              </>
            ) : (
              <>
                {change.value > 0 ? '+' : ''}
                {change.value} {change.comparisonPeriod}
              </>
            )}
          </div>
        </div>
      </div>
    </CustomCard>
  );
};
